import { useEffect, useMemo, useRef, useState } from 'react';
import { useFormik } from 'formik';
import { initialValues, validationSchema } from './formData';
import { PqrStatusManagement } from '../../../../../../shared/types/modules/pqr';
import { PqrStatusManagementProps } from './props';
import dayjs from 'dayjs';
import { ISelect, ParamEnum } from '../../../../../../shared/types/shared';
import {
    asyncGetParamById,
    asyncGetParamPqrTimes,
    asyncGetParams,
} from '../../../../../../services/param.service';
import {
    calculateDateWithDays,
    isBusinessDay,
} from '../../../../../../shared/utils/date';
import { Template } from '../../../../../../shared/types/template';
import { asyncGetTemplates } from '../../../../../../services/template.service';
import { confirmSwalGeneric } from '../../../../../components/general/sweetAlert/confirmSwal';
import { useAuthContext } from '../../../../../../contexts/authContext';

export const usePqrStatusManagement = ({
    data,
    onChangeData,
    onChangeFormik,
    createdAt,
    status,
    receptionChannel,
    type,
    half,
    subtypeDp,
    procedure,
    onIsWorngMovement,
}: PqrStatusManagementProps) => {
    const [originalStatusList, setOriginalStatusList] = useState<ISelect[]>([]);
    const [statusList, setStatusList] = useState<ISelect[]>([]);
    const [lastStatusParam, setLastStatusParam] = useState<ISelect>();
    const [statusParam, setStatusParam] = useState<ISelect>();
    const [types, setTypes] = useState<ISelect[]>([]);
    const [templates, setTemplates] = useState<Template[]>([]);
    const [selectTemplate, setSelectTemplate] = useState<Template | null>(null);
    const [changeReplicaToProcessed, setChangeReplicaToProcessed] =
        useState(false);
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [message, setMessage] = useState<string>();
    const { modeTheme } = useAuthContext();

    const onSubmit = (data: PqrStatusManagement) => {
        if (onChangeData) onChangeData(data || {});
    };

    const getStatusParamById = (id: string) =>
        statusList.find((s) => s.id === id);

    const getStatusParamByIdFromOriginalList = (id: string) =>
        originalStatusList.find((s) => s.id === id);

    const getTypeParamByCode = (code: string) =>
        types.find((s) => s.code === code);
    const defaultTemplate = `<!DOCTYPE html>
            <html lang="es">
            <head>
                <meta charset="UTF-8">
                <meta name="viewport" content="width=device-width, initial-scale=1.0">
                <title>Plantilla de Edición de prórroga </title>
                <style>
                    body {
                        font-family: Arial, sans-serif;
                        line-height: 1.6;
                        margin: 40px;
                    }
                    .content {
                        margin-bottom: 20px;
                    }
                    
                </style>
            </head>
            <body>
                <div class="content">
                    <p>PREGUNTAR SIEMPRE:</p>
                    <p>¿Es usted funcionario público?</p>
                    <br>
                    <p>Cedula contacto:</p>
                    <p>Nombre contacto:</p>
                    <p>Cédula o Nit:</p>
                    <p>Nombre o Razón social:</p>
                    <p>Teléfono:</p>
                    <p>Correo:</p>
                    <p>Dirección:</p>
                    <p>Ciudad:</p>
                    <p>Valor a Asegurar:</p>
                    <p>Objeto o Fin del seguro:</p>
                    <p>Datos adicionales importantes para la cotización</p>
                    <p>Es importante dejar en las observaciones el objetivo o fin de la póliza.</p>
                </div>
            </body>
            </html>`;

    const isClosingStatus = statusParam?.code === `${ParamEnum.PQR_STATUS}_CER`;
    const isFollowUpStatus =
        statusParam?.code === `${ParamEnum.PQR_STATUS}_SEG`;
    const isStatusExtensionInfo =
        statusParam?.code === `${ParamEnum.PQR_STATUS}_AMP`;
    const isStatusReceptionInfo =
        statusParam?.code === `${ParamEnum.PQR_STATUS}_REC`;
    const isStatusSolution =
        statusParam?.code === `${ParamEnum.PQR_STATUS}_SOL`;
    const isStatusOpen = statusParam?.code === `${ParamEnum.PQR_STATUS}_ABI`;
    const isStatusProcessed =
        statusParam?.code === `${ParamEnum.PQR_STATUS}_TRA`;
    const isStatusExtension =
        statusParam?.code === `${ParamEnum.PQR_STATUS}_PRO`;
    const isStatusCancelled =
        statusParam?.code === `${ParamEnum.PQR_STATUS}_ANU`;
    const isTypeQOR =
        Boolean(type) &&
        type === getTypeParamByCode(`${ParamEnum.PQR_TYPE}_QOR`)?.id;

    const minClosedDate = isTypeQOR
        ? dayjs(createdAt).add(1, 'days')
        : dayjs(createdAt);

    const isStatusProcessedInFirstLoad =
        statusParam?.code === `${ParamEnum.PQR_STATUS}_TRA` && !lastStatusParam;

    const maxClosedDate = dayjs();

    const formik = useFormik({
        initialValues,
        validationSchema: validationSchema(
            isClosingStatus,
            isFollowUpStatus,
            isStatusExtensionInfo,
            isStatusReceptionInfo,
            isStatusSolution,
            isStatusProcessed,
            isStatusExtension,
            createdAt,
            changeReplicaToProcessed
        ),
        onSubmit,
    });

    const prevStatusRef = useRef<any>(formik.values.status);
    const prevLabelRef = useRef<string>(formik.values.label ?? '');

    const maxNotificationProcedureDate =
        formik.values.notificationProcedureDate &&
        dayjs(formik.values.notificationProcedureDate).isAfter(dayjs(createdAt))
            ? dayjs(formik.values.notificationProcedureDate)
            : dayjs(createdAt);

    const minSuspensionDate = [
        formik.values.notificationProcedureDate,
        formik.values.processingDate,
        createdAt,
    ]
        .filter((date) => date)
        .map((date) => dayjs(date))
        .reduce(
            (max, date) => (date.isAfter(max) ? date : max),
            dayjs(createdAt)
        );

    const minReactivationDate = [
        minSuspensionDate,
        formik.values.suspensionDate,
    ]
        .filter((date) => date)
        .map((date) => dayjs(date))
        .reduce(
            (max, date) => (date.isAfter(max) ? date : max),
            dayjs(createdAt)
        );

    const enableReactivationDate = useMemo(() => {
        const creationDate = dayjs(createdAt);
        const cutoffDate = dayjs('2025-02-01');

        const statusCode = statusList.find(
            (s) => s.id === formik.values.status
        )?.code;

        const isSolved = statusCode === `${ParamEnum.PQR_STATUS}_SOL`;
        const isBeforeCutoff = creationDate.isBefore(cutoffDate);

        return isSolved && isBeforeCutoff;
    }, [formik.values.status, statusList, createdAt]);

    const minNotificationResponseDate = [
        minSuspensionDate,
        formik.values.responseDate,
    ]
        .filter((date) => date)
        .map((date) => dayjs(date))
        .reduce(
            (max, date) => (date.isAfter(max) ? date : max),
            dayjs(createdAt)
        );

    const minResponseDate = [
        minSuspensionDate,
        formik.values.reactivationDate,
        formik.values.extensionDate,
    ]
        .filter((date) => date)
        .map((date) => dayjs(date))
        .reduce(
            (max, date) => (date.isAfter(max) ? date : max),
            dayjs(createdAt)
        );

    // Función helper para asignar fechas
    const setDate = (
        field: string,
        days: number,
        typeDays: boolean,
        startDate?: Date,
        newStatusCode?: string
    ) => {
        let adjustedStartDate = startDate;
        let adjustedDays = days;

        if (startDate && !isBusinessDay(startDate)) {
            let next = dayjs(startDate).add(1, 'day');
            while (!isBusinessDay(next.toDate())) {
                next = next.add(1, 'day');
            }
            adjustedStartDate = next.toDate();
            adjustedDays = Math.max(days - 1, 0);

            if (newStatusCode === `${ParamEnum.PQR_STATUS}_ABI`) {
                adjustedDays = Math.max(days - 1, 0);
            }
        }

        formik.setFieldValue(
            field,
            calculateDateWithDays(
                adjustedDays,
                adjustedStartDate,
                typeDays,
                typeDays
            )
        );
    };

    const isSameDate = (a?: any, b?: any) => {
        if (!a && !b) return true;
        if (!a || !b) return false;
        return dayjs(a).isSame(dayjs(b), 'minute');
    };

    const getDates = async () => {
        const statusParam = getStatusParamById(formik.values.status as string);
        const isChangeStatus = statusParam?.id !== status;

        const baseDatesChanged =
            !isSameDate(data?.extensionDate, formik.values.extensionDate) ||
            !isSameDate(
                data?.suspensionDate ?? undefined,
                formik.values.suspensionDate
            ) ||
            !isSameDate(
                data?.reactivationDate,
                formik.values.reactivationDate
            ) ||
            !isSameDate(
                data?.customerFilingDate,
                formik.values.customerFilingDate
            );

        const skipCalculation =
            (data?.sacDate &&
                data?.furagDate &&
                !isChangeStatus &&
                !baseDatesChanged) ||
            !createdAt ||
            !statusParam ||
            !statusParam.code ||
            !receptionChannel ||
            !type ||
            !half ||
            !subtypeDp ||
            !procedure ||
            [
                `${ParamEnum.PQR_STATUS}_SOL`,
                `${ParamEnum.PQR_STATUS}_CER`,
                `${ParamEnum.PQR_STATUS}_SEG`,
                `${ParamEnum.PQR_STATUS}_ANU`,
            ].includes(statusParam.code);

        if (skipCalculation) {
            const sacDate = data?.sacDate ?? data?.customerFilingDate;
            setDate('sacDate', 0, true, sacDate);

            const furagDate = data?.furagDate ?? data?.customerFilingDate;
            setDate('furagDate', 0, false, furagDate);
            return;
        }

        const initialStatus = getStatusParamByIdFromOriginalList(
            status as string
        );
        const newStatusCode = statusParam.code;

        const isStatusExtension =
            newStatusCode === `${ParamEnum.PQR_STATUS}_PRO`;

        const isReactivationCase =
            (initialStatus?.code === `${ParamEnum.PQR_STATUS}_AMP` &&
                newStatusCode === `${ParamEnum.PQR_STATUS}_REC`) ||
            (initialStatus?.code === `${ParamEnum.PQR_STATUS}_REC` &&
                newStatusCode === `${ParamEnum.PQR_STATUS}_TRA`);

        let startDate = null;
        if (isReactivationCase) {
            //Ampliación Info. -> Recepción Info.= Fecha reactivación
            //Recepción Info. -> Tramitado = Fecha reactivación
            startDate = formik.values.reactivationDate ?? null;
        } else if (isStatusExtension) {
            //prórroga
            startDate = formik.values.extensionDate ?? null;
        } else if (newStatusCode === `${ParamEnum.PQR_STATUS}_AMP`) {
            startDate = formik.values.suspensionDate ?? null;
        } else {
            startDate = formik.values.customerFilingDate ?? null;
        }

        if (!startDate) {
            formik.setFieldValue('sacDate', null);
            formik.setFieldValue('furagDate', null);
            return;
        }

        const times = await asyncGetParamPqrTimes({
            status: formik.values.status as string,
            receptionChannel,
            type,
            half,
            subtypeDp,
            procedure,
        });

        if (times) {
            if (times.furagTime) {
                const days = Number(times.furagTime);
                const typeDays =
                    (times.typeClassificationFuragTime as ISelect).code !==
                    'ALERTS_CLASSIFICATION_WORKING_DAYS';
                setDate(
                    'furagDate',
                    days,
                    typeDays,
                    startDate,
                    statusParam.code
                );
            }
            if (times.sacTime) {
                const days = Number(times.sacTime);
                const typeDays =
                    (times.typeClassificationSacTime as ISelect).code !==
                    'ALERTS_CLASSIFICATION_WORKING_DAYS';
                setDate('sacDate', days, typeDays, startDate, statusParam.code);
            }
        } else {
            if (
                newStatusCode &&
                newStatusCode === `${ParamEnum.PQR_STATUS}_AMP`
            ) {
                const days = 30;
                const typeDays = true;
                setDate(
                    'sacDate',
                    days,
                    typeDays,
                    data?.sacDate ?? startDate,
                    statusParam.code
                );
                setDate(
                    'furagDate',
                    days,
                    !typeDays,
                    data?.furagDate ?? startDate,
                    statusParam.code
                );
            } else {
                if (isChangeStatus) {
                    setMessage(`
                        No existe una regla en <strong>Módulo Estados PQR</strong> asociada a esta combinación:
                        <ul style="margin-top: 8px; margin-bottom: 8px; padding-left: 20px;">
                            <li><strong>Estado de la Regla</strong></li>
                            <li><strong>Canal de Recepción</strong></li>
                            <li><strong>Tipo PQR</strong></li>
                            <li><strong>Medio</strong></li>
                            <li><strong>Subtipo DP</strong></li>
                            <li><strong>Trámite</strong></li>
                        </ul>
                        Puede que el cálculo de las fechas <strong>SAC</strong> y <strong>Normativa</strong> no se haya ejecutado correctamente.
                        `);
                    setIsOpen(true);
                }
                setDate(
                    'sacDate',
                    0,
                    true,
                    data?.sacDate ?? startDate,
                    statusParam.code
                );
                setDate(
                    'furagDate',
                    0,
                    false,
                    data?.furagDate ?? startDate,
                    statusParam.code
                );
            }
        }
    };

    useEffect(() => {
        getDates();
    }, [
        formik.values.extensionDate,
        formik.values.suspensionDate,
        formik.values.reactivationDate,
        formik.values.customerFilingDate,
        formik.values.status,
    ]);

    const STATUS_WORNG_MOVEMENT = [`${ParamEnum.PQR_STATUS}_AMP`];

    const dateRadication = formik.values.customerFilingDate
        ? dayjs(formik.values.customerFilingDate)
        : null;

    const daysSinceRadication = dateRadication
        ? dayjs().diff(dateRadication, 'day')
        : null;

    const isWorngMovement = Boolean(
        statusParam &&
            !data?.suspensionDate &&
            daysSinceRadication !== null &&
            daysSinceRadication > 10 &&
            STATUS_WORNG_MOVEMENT.includes(statusParam?.code || '')
    );

    const getTemplates = async () => {
        const templates = await asyncGetTemplates();
        setTemplates(templates);
    };

    useEffect(() => {
        onIsWorngMovement(isWorngMovement);
    }, [isWorngMovement]);

    useEffect(() => {
        if (onChangeFormik) onChangeFormik(formik);
    }, [formik.values, formik.errors]);

    useEffect(() => {
        formik?.setFieldValue(
            'customerFilingDate',
            data?.customerFilingDate
                ? dayjs(data?.customerFilingDate)
                : dayjs(createdAt).subtract(5, 'hours')
        );
        formik?.setFieldValue(
            'notificationProcedureDate',
            data?.notificationProcedureDate
                ? dayjs(data?.notificationProcedureDate)
                : dayjs(createdAt).subtract(5, 'hours')
        );
        formik?.setFieldValue(
            'processingDate',
            data?.processingDate ? dayjs(data?.processingDate) : undefined
        );
        formik?.setFieldValue(
            'suspensionDate',
            data?.suspensionDate ? dayjs(data?.suspensionDate) : undefined
        );
        formik?.setFieldValue(
            'sendEmailExpansionInformation',
            data?.sendEmailExpansionInformation || ''
        );
        formik?.setFieldValue(
            'reactivationDate',
            data?.reactivationDate ? dayjs(data?.reactivationDate) : undefined
        );
        formik?.setFieldValue(
            'extensionDate',
            data?.extensionDate ? dayjs(data?.extensionDate) : undefined
        );
        formik?.setFieldValue(
            'sendEmailExtension',
            data?.sendEmailExtension || ''
        );
        formik?.setFieldValue(
            'alertSFCDate',
            data?.alertSFCDate ? dayjs(data?.alertSFCDate) : undefined
        );
        formik?.setFieldValue(
            'responseDate',
            data?.responseDate ? dayjs(data?.responseDate) : undefined
        );
        formik?.setFieldValue(
            'sendEmailResponse',
            data?.sendEmailResponse || ''
        );
        formik?.setFieldValue(
            'notificationResponseDate',
            data?.notificationResponseDate
                ? dayjs(data?.notificationResponseDate)
                : undefined
        );
        formik?.setFieldValue(
            'sacDate',
            data?.sacDate ? dayjs(data?.sacDate) : undefined
        );
        formik?.setFieldValue(
            'furagDate',
            data?.furagDate ? dayjs(data?.furagDate) : undefined
        );
        formik?.setFieldValue(
            'closingDate',
            data?.closingDate ? dayjs(data?.closingDate) : undefined
        );
        formik?.setFieldValue(
            'editFuragAndSac',
            data?.editFuragAndSac || false
        );
    }, [data]);

    useEffect(() => {
        formik.setFieldValue('status', status || '');
    }, [status]);

    useEffect(() => {
        getTemplates();

        const asyncStatus = getStatusParamById(formik.values.status as string);
        if (statusParam && statusParam?.code !== asyncStatus?.code)
            setLastStatusParam(statusParam);
        setStatusParam(asyncStatus);
    }, [
        createdAt,
        formik.values.status,
        receptionChannel,
        type,
        half,
        subtypeDp,
        procedure,
        data,
        formik.values.reactivationDate,
    ]);

    useEffect(() => {
        asyncGetParams(ParamEnum.PQR_STATUS).then((items) => {
            setStatusList(items);
            setOriginalStatusList(items);
        });
        asyncGetParams(ParamEnum.PQR_TYPE).then((items) => setTypes(items));
    }, []);

    useEffect(() => {
        if (isClosingStatus && !formik.values.closingDate)
            formik.setFieldValue('closingDate', dayjs());
    }, [isClosingStatus]);

    const filteredStatusList = useMemo(() => {
        let updatedStatusList: ISelect[] = JSON.parse(
            JSON.stringify(originalStatusList)
        );
        let isUpdated = false;

        if (
            statusParam !== undefined &&
            statusParam?.code !== `${ParamEnum.PQR_STATUS}_REP`
        ) {
            updatedStatusList = updatedStatusList.filter(
                (statusItem) =>
                    statusItem.code !== `${ParamEnum.PQR_STATUS}_REP`
            );
            isUpdated = true;
        }

        if (!data?.suspensionDate) {
            updatedStatusList = updatedStatusList.filter(
                (statusItem) =>
                    statusItem.code !== `${ParamEnum.PQR_STATUS}_REC`
            );
            isUpdated = true;
        }

        if (isUpdated) {
            return updatedStatusList;
        }

        return originalStatusList;
    }, [statusParam, originalStatusList, data?.suspensionDate]);

    // Update statusList when filteredStatusList changes
    useEffect(() => {
        setStatusList(filteredStatusList);
    }, [filteredStatusList]);

    useEffect(() => {
        if (
            lastStatusParam?.code === `${ParamEnum.PQR_STATUS}_AMP` &&
            statusParam?.code === `${ParamEnum.PQR_STATUS}_SOL`
        ) {
            formik.setFieldValue('suspensionDate', '');
        }
    }, [lastStatusParam, statusParam]);

    useEffect(() => {
        const fetchHalfParam = async () => {
            if (!half || !isStatusSolution) return;

            const halfParam = await asyncGetParamById(half as string);
            const isDCF = halfParam?.name === 'DCF';

            if (isDCF || formik?.values?.responseDate) return;
            formik.setFieldValue('responseDate', dayjs());
        };

        fetchHalfParam();
    }, [half, statusParam]);

    useEffect(() => {
        if (status) {
            const initialStatus = getStatusParamByIdFromOriginalList(
                status as string
            );
            if (
                initialStatus?.code === `${ParamEnum.PQR_STATUS}_REP` &&
                statusParam?.code === `${ParamEnum.PQR_STATUS}_TRA`
            ) {
                setChangeReplicaToProcessed(true);
            }
        }
    }, [statusParam]);

    useEffect(() => {
        let isMounted = true;

        const run = async () => {
            const rawStatus = formik.values.status;
            const statusId =
                typeof rawStatus === 'string'
                    ? rawStatus
                    : typeof rawStatus === 'object' && rawStatus?.id
                        ? String(rawStatus.id)
                        : '';

            if (!statusId) return;

            const currentStatus = getStatusParamByIdFromOriginalList(statusId);
            const nextLabel = currentStatus?.name ?? '';

            if (!nextLabel) return;

            if (nextLabel.toUpperCase() === 'ANULADO') {
                const confirmed = await confirmSwalGeneric({
                    title: 'Cambiar estado a ANULADO',
                    html:
                        'Está a punto de marcar esta PQR como <b>ANULADA</b>.<br>' +
                        '¿Está Seguro(a) que desea continuar?',
                    modeTheme,
                });

                if (!isMounted) return;

                if (confirmed) {
                    await formik.setFieldValue('label', nextLabel);
                    prevStatusRef.current = formik.values.status;
                    prevLabelRef.current = nextLabel;
                } else {
                    await formik.setFieldValue(
                        'status',
                        prevStatusRef.current ?? ''
                    );
                    await formik.setFieldValue(
                        'label',
                        prevLabelRef.current ?? ''
                    );
                }
                return;
            }

            if (!formik.values.label) {
                await formik.setFieldValue('label', nextLabel);
                prevLabelRef.current = nextLabel;
            }
            prevStatusRef.current = formik.values.status;
        };

        run();

        return () => {
            isMounted = false;
        };
    }, [formik.values.status]);

    useEffect(() => {
        if (
            (templates.length > 0 &&
                formik.values.sendEmailExpansionInformation &&
                isStatusExtensionInfo) ||
            (formik.values.sendEmailExtension && isStatusExtension) ||
            (formik.values.sendEmailResponse && isStatusSolution)
        ) {
            const templateType = isStatusExtensionInfo
                ? ParamEnum.PQR_EDIT_AMP
                : isStatusExtension
                    ? ParamEnum.PQR_EDIT_PRO
                    : ParamEnum.PQR_EDIT_SOL;
            const template = templates.find(
                (template) => template.code === templateType
            );
            if (template) {
                formik.setFieldValue('template', template.body);
                setSelectTemplate(template);
                formik.setFieldValue('templateId', template.id);
                formik.setFieldValue('templateType', templateType);
            } else {
                formik.setFieldValue('template', defaultTemplate);
                setSelectTemplate(null);
                formik.setFieldValue('templateId', '');
                formik.setFieldValue('templateType', templateType);
            }
        }
    }, [
        formik.values.sendEmailExpansionInformation,
        isStatusExtensionInfo,
        formik.values.sendEmailExtension,
        isStatusExtension,
        formik.values.sendEmailResponse,
        isStatusSolution,
        formik.values.status,
    ]);

    const handleClose = () => {
        setIsOpen(false);
        setMessage('');
    };

    return {
        formik,
        statusList,
        isClosingStatus,
        isFollowUpStatus,
        isStatusExtensionInfo,
        isStatusReceptionInfo,
        isStatusSolution,
        isStatusOpen,
        isStatusProcessed,
        isStatusExtension,
        isStatusCancelled,
        isTypeQOR,
        minClosedDate,
        maxClosedDate,
        maxNotificationProcedureDate,
        minSuspensionDate,
        minReactivationDate,
        minNotificationResponseDate,
        minResponseDate,
        isStatusProcessedInFirstLoad,
        isWorngMovement,
        enableReactivationDate,
        selectTemplate,
        isOpen,
        handleClose,
        message,
    };
};
